/* ==========================================================================
   as-boho-queens.js
   PDP — Boho Queens, out in the world.

   Two behaviours across the breakpoint, and only one of them is JavaScript:

     desktop  a continuous ticker. The drift is a CSS animation over a doubled
              set of tiles — see the section's @keyframes — so all this does is
              clone the set once and work out how long one cycle should take.
              Swiper is not initialised up here at all.

     mobile   the same drift by default, or a plain swipe rail with snapping
              and the next tile peeking when "keep scrolling on mobile" is
              off — which is what the collection page wants, per its notes.
              The swipe rail is Swiper, with no autoplay of its own.

   WHY THE DRIFT IS NOT SWIPER. It used to be Swiper's autoplay with delay 0,
   which re-arms each step from the previous step's transitionend. That makes
   the ticker one swallowed event away from stopping — and Swiper's own guards
   then make it permanent, since start() refuses to run while an old timeout id
   survives and pause() refuses while `paused` is set, which stop() never
   clears. A resize, a tab switch and a hover could each land there, and the
   only honest fix was to stop depending on it. A compositor animation has no
   timer to miss and no flags to get stuck in; if this file never runs, the
   tiles still drift, just at the fallback duration.

   Nothing pauses the drift but leaving the viewport and a background tab.
   Hover deliberately does not: a ticker that stops under her cursor is
   indistinguishable from a broken one.

   SOUND. Dev notes: some clips have no audio, and a sound control must not
   appear on those. The control is rendered per tile from a merchant checkbox;
   this only wires up the toggle. Unmuting is user-initiated, so autoplay
   policies allow it, and only one clip is ever audible at a time.

   Tile playback goes through <as-lazy-video>, so the clones share the
   page-wide six-decoder budget rather than adding their own.
   ========================================================================== */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 990px)';

  class AsBohoQueens extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-queens-rail]');
      this.wrapper = this.rail && this.rail.querySelector('.swiper-wrapper');
      if (!this.wrapper) return;

      this.pxPerSec = parseFloat(this.dataset.speed) || 0;
      this.gap = parseInt(this.dataset.gap, 10) || 0;

      /* Whether the drift is allowed below the desktop breakpoint. Default is
         on: the attribute is only ever absent on markup rendered before this
         setting existed, and drifting is what those instances did on desktop
         anyway. */
      this.driftOnMobile = this.dataset.autoscrollMobile !== 'false';

      this.desktopMQ = window.matchMedia(DESKTOP);
      this.reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.inView = false;
      this.ticker = false;
      this.swiper = null;
      this.frame = null;

      this._build = this._build.bind(this);
      this._sync = this._sync.bind(this);
      this._onResize = this._onResize.bind(this);

      this._build();
      this._bindSound();
      this._bindPlay();

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            this._sync();
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(this);

      document.addEventListener('visibilitychange', this._sync);
      window.addEventListener('resize', this._onResize, { passive: true });
      this._listen(this.desktopMQ, this._build);
      this._listen(this.reduceMQ, this._build);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this._sync);
      window.removeEventListener('resize', this._onResize);
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.swiper) this.swiper.destroy(true, true);
    }

    _listen(mq, fn) {
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', fn);
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(fn);
      }
    }

    _build() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this._removeClones();

      var count = this.wrapper.querySelectorAll('.swiper-slide').length;
      /* The drift is a CSS animation keyed off .is-ticker and is not gated by
         any media query, so letting it run on mobile is only this condition —
         nothing in the stylesheet had to change. Reduced motion still wins. */
      var wide = this.desktopMQ.matches || this.driftOnMobile;
      var ticker = wide && this.pxPerSec > 0 && !this.reduceMQ.matches && count > 0;

      this.ticker = ticker;
      this.classList.toggle('is-ticker', ticker);

      if (ticker) {
        this._clone();
        this._measure();
      } else if (typeof window.Swiper !== 'undefined') {
        this.swiper = new window.Swiper(this.rail, {
          slidesPerView: 'auto',
          spaceBetween: this.gap,
          grabCursor: true,
          // A horizontal drag must never steal vertical scroll.
          touchAngle: 30,
          touchStartPreventDefault: false,
          passiveListeners: true
        });
      }

      this._sync();
    }

    /* The animation travels -50%, so the rail has to hold the set twice: the
       second copy is what the eye reads as the first one coming back round.
       Cloning here rather than in Liquid keeps the section rendering one set.

       ONE SET HAS TO OUTRUN THE RAIL. This is the same rule the UGC wall
       states as "one set has to outrun the rail, or the wrap would rewind into
       a gap". At -50% the rewind is invisible only while the half being
       rewound into is already covering the screen; if a set is narrower than
       the viewport — which is what happens on a phone, where the tiles are a
       fraction of the width and there may only be a handful — you watch it
       snap back to the beginning.

       So the set is repeated until it is wider than the rail, and only then
       doubled. Desktop almost always needs one repeat and is unchanged. */
    _clone() {
      var originals = Array.prototype.slice.call(this.wrapper.children);
      if (!originals.length) return;

      var setWidth = this.wrapper.getBoundingClientRect().width;
      var railWidth = (this.rail && this.rail.getBoundingClientRect().width) || window.innerWidth;

      var reps = 1;
      if (setWidth > 0) reps = Math.max(1, Math.ceil(railWidth / setWidth));

      // reps copies make one set; the whole thing is then doubled for the wrap.
      var copies = reps * 2 - 1;
      for (var i = 0; i < copies; i++) {
        originals.forEach((tile) => {
          var copy = tile.cloneNode(true);
          copy.setAttribute('data-queens-clone', '');
          this.wrapper.appendChild(copy);
        });
      }
    }

    _removeClones() {
      this.querySelectorAll('[data-queens-clone]').forEach((clone) => {
        clone.remove();
      });
    }

    /* One cycle is one set of tiles, and the tiles are clamped against the
       viewport, so the pace the editor asked for in px/sec can only be turned
       into a duration once they have laid out. Half the wrapper is one set,
       trailing margins and all. */
    _measure() {
      var width = this.wrapper.getBoundingClientRect().width / 2;
      if (!width || !this.pxPerSec) return;
      this.style.setProperty('--as-queens-dur', (width / this.pxPerSec).toFixed(2) + 's');
    }

    /* Tile widths track the viewport, so a resize changes how far one cycle
       travels and the duration has to follow it to hold the same px/sec. */
    _onResize() {
      if (!this.ticker || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this._measure();
      });
    }

    /* A marquee running in a background tab or below the fold is wasted work.
       The animation resumes from where it paused, so there is nothing here
       that can leave the ticker stopped. */
    _sync() {
      this.toggleAttribute('data-paused', !this.ticker || !this.inView || document.hidden);
    }

    /* Tap-to-play, for when mobile autoplay is switched off.

       Delegated for the same reason as the sound control: the desktop drift
       runs on cloned tiles, and a clone's button has to behave like the
       original's. The clip is started through the <as-lazy-video> element
       rather than the <video> directly, so it goes through the page-wide
       decoder budget instead of around it. */
    _bindPlay() {
      this.addEventListener('click', (event) => {
        var button = event.target.closest('[data-queens-play]');
        if (!button) return;

        event.preventDefault();

        var tile = button.closest('[data-queens-tile]');
        var host = tile && tile.querySelector('as-lazy-video');
        if (!host || typeof host.start !== 'function') return;

        host.start();

        // Hidden on this tile only. A second tile tapped later plays too —
        // the budget decides how many actually run, not this.
        button.hidden = true;
      });
    }

    _bindSound() {
      // Delegated: the drift runs on cloned tiles, and a clone's button has to
      // work exactly like the original's.
      this.addEventListener('click', (event) => {
        var button = event.target.closest('[data-queens-sound]');
        if (!button) return;

        event.preventDefault();

        var tile = button.closest('[data-queens-tile]');
        var video = tile && tile.querySelector('video');
        if (!video) return;

        var turningOn = video.muted;

        // Only one clip audible at a time.
        this.querySelectorAll('video').forEach((other) => {
          other.muted = true;
        });
        this.querySelectorAll('[data-queens-sound]').forEach((other) => {
          other.setAttribute('aria-pressed', 'false');
        });

        if (turningOn) {
          video.muted = false;
          var played = video.play();
          if (played && typeof played.catch === 'function') played.catch(function () {});
          button.setAttribute('aria-pressed', 'true');
        }

        this._syncSoundLabels();
      });

      this._syncSoundLabels();
    }

    _syncSoundLabels() {
      this.querySelectorAll('[data-queens-sound]').forEach((button) => {
        var on = button.getAttribute('aria-pressed') === 'true';
        button.setAttribute('aria-label', on ? 'Mute video' : 'Play sound');
      });
    }
  }

  if (!customElements.get('as-boho-queens')) {
    customElements.define('as-boho-queens', AsBohoQueens);
  }
})();
